import { Routes, Route } from "react-router-dom";
import { AuthGuard } from "./guards/AuthGuard";
import { PublicGuard } from "./guards/PublicGuard";
import { OnboardingGuard } from "./guards/OnboardingGuard";
import { ApplicationGuard } from "./guards/ApplicationGuard";
import { OfferGuard } from "./guards/OfferGuard";
import { LoanGuard } from "./guards/LoanGuard";
import { ChatStepGuard } from "./guards/ChatStepGuard";
import { ChatPage } from "@/features/chat";
import {
  SignUpPage,
  PhoneConfirmationPage,
  LogoutPage,
  AppRedirectPage,
} from "@/features/auth";
import {
  BrokerOfferViewPage,
  BrokerConfirmPhonePage,
  BrokerOtpPage,
} from "@/features/brokerOffer";
import {
  OnboardingTermsPage,
  OfferTermsPage,
  AnafTermsPage,
  AnafConsentPage,
  BcTermsPage,
  PartnerTermsPage,
} from "@/features/terms";
import { CnpPage } from "@/features/onboarding";
import { FinancialNeedPage, DesiredAmountPage } from "@/features/questionnaire";
import {
  KycPage,
  KycInProgressPage,
  KycFailedPage,
  ManualAddressPage,
  ProofOfAddressPage,
} from "@/features/kyc";
import { QuestionnaireGuard } from "./guards/QuestionnaireGuard";
import {
  ApplicationPage,
  CalculatorPage,
  CreditLineAmountPage,
  CreditLineConfirmationPage,
  CreditLinePaymentDatePage,
  DerogationDeclarationPage,
  AlternativeOfferPage,
  InstallmentConfirmationPage,
} from "@/features/application";
import {
  EmailPage,
  EmailConfirmationPage,
  EmailLinkValidationPage,
} from "@/features/email";
import {
  NoOfferAvailablePage,
  VoltAlternativePage,
  VoltZeroLandingPage,
  RefinanceLandingPage,
  OfferInProgressPage,
  AnafInProgressPage,
  ProofOfIncomePage,
  PreliminaryOfferPage,
  PreliminaryNoOfferPage,
  PreliminaryNoScorePage,
  PreliminaryRefinancePage,
  PartnerOfferDetailsPage,
  PreliminaryCalculatorPage,
  PreliminaryAppRedirectPage,
} from "@/features/offer/pages";
import {
  IncomePage,
  BcConsentPage,
  ScoringStartPage,
  BcPhoneSelectPage,
  BcPhoneCodePage,
} from "@/features/scoring";
import { ScoringGuard } from "./guards/ScoringGuard";
import { EmailGuard } from "./guards/EmailGuard";
import { SignLoanPage, ActiveLoanPage } from "@/features/loan";
import { AppLayout } from "@/layouts/AppLayout/AppLayout";
import { KycGuard } from "./guards/KycGuard";
import {
  AddBankCardPage,
  AddBankCardResultPage,
} from "@/features/bankCard/pages";
import { PageViewTracker } from "@/components/PageViewTracker";
import { ScrollToTop } from "@/components/ScrollToTop";

export function AppRouter() {
  return (
    <>
      <PageViewTracker />
      <ScrollToTop />
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="logout" element={<LogoutPage />} />

          <Route path="signup" element={<SignUpPage />} />
          <Route
            path="phone-confirmation"
            element={<PhoneConfirmationPage />}
          />

          <Route
            path="email-validation"
            element={<EmailLinkValidationPage />}
          />

          <Route
            path="broker-offer/:offerId"
            element={<BrokerOfferViewPage />}
          />
          <Route
            path="broker-offer/:offerId/confirm-phone"
            element={<BrokerConfirmPhonePage />}
          />
          <Route
            path="broker-offer/:offerId/otp"
            element={<BrokerOtpPage />}
          />

          <Route element={<AuthGuard />}>
            <Route path="app-redirect" element={<AppRedirectPage />} />

            <Route path="wizard" element={<ChatPage />} />

            <Route element={<ChatStepGuard />}>
              <Route path={"onboarding/*"} element={<OnboardingGuard />}>
                <Route path="cnp" element={<CnpPage />} />
              </Route>

              <Route path="questionnaire" element={<QuestionnaireGuard />}>
                <Route path="financial-need" element={<FinancialNeedPage />} />
                <Route path="desired-amount" element={<DesiredAmountPage />} />
              </Route>

              <Route path="scoring" element={<ScoringGuard />}>
                <Route index element={<ScoringStartPage />} />
                <Route path="income" element={<IncomePage />} />
                <Route path="terms" element={<BcConsentPage />} />
                <Route path="phones" element={<BcPhoneSelectPage />} />
                <Route path="code" element={<BcPhoneCodePage />} />
              </Route>

              <Route path="kyc" element={<KycGuard />}>
                <Route index element={<KycPage />} />
                <Route path="in-progress" element={<KycInProgressPage />} />
                <Route path="failed" element={<KycFailedPage />} />
                <Route path="manual-address" element={<ManualAddressPage />} />
                <Route path="proof-of-address" element={<ProofOfAddressPage />} />
              </Route>

              <Route path="terms" element={<OnboardingTermsPage />} />
          
              <Route path="email" element={<EmailGuard />}>
                <Route index element={<EmailPage />} />
                <Route path="confirmation" element={<EmailConfirmationPage />} />
              </Route>

              <Route path="terms/anaf" element={<AnafTermsPage />}></Route>
              <Route path="terms/anaf-consent" element={<AnafConsentPage />} />
              <Route path="terms/bc" element={<BcTermsPage />}></Route>
              <Route path="terms/partner" element={<PartnerTermsPage />}></Route>

              <Route path="bank-card/add" element={<AddBankCardPage />}></Route>
              <Route
                path="bank-card/add/result/:resultType/*"
                element={<AddBankCardResultPage />}
              ></Route>
              <Route
                path="bank-card/add/failed/:resultType"
                element={<AddBankCardResultPage />}
              ></Route>

              <Route
                path="offer/preliminary"
                element={<PreliminaryOfferPage />}
              />
              <Route
                path="offer/preliminary/not-available"
                element={<PreliminaryNoOfferPage />}
              />
              <Route
                path="offer/preliminary/no-score"
                element={<PreliminaryNoScorePage />}
              />
              <Route
                path="offer/preliminary/refinance"
                element={<PreliminaryRefinancePage />}
              />
              <Route
                path="offer/preliminary/refinance/details"
                element={<RefinanceLandingPage />}
              />
              <Route
                path="offer/preliminary/volt-zero"
                element={<VoltZeroLandingPage />}
              />
              <Route
                path="offer/preliminary/:type"
                element={<PartnerOfferDetailsPage />}
              />
              <Route
                path="offer/preliminary/app-redirect"
                element={<PreliminaryAppRedirectPage />}
              />
              <Route path="offer/preliminary/calculator">
                <Route index element={<PreliminaryCalculatorPage />} />
                <Route path=":tab" element={<PreliminaryCalculatorPage />} />
              </Route>

              <Route path="offer" element={<OfferGuard />}>
                <Route path="terms" element={<OfferTermsPage />} />
                <Route path="in-progress" element={<OfferInProgressPage />} />
                <Route path="anaf-in-progress" element={<AnafInProgressPage />} />
                <Route path="not-available" element={<NoOfferAvailablePage />} />
                <Route path="volt-alternative" element={<VoltAlternativePage />} />
                <Route
                  path="volt-alternative/volt-zero"
                  element={<VoltZeroLandingPage />}
                />
                <Route path="proof-of-income" element={<ProofOfIncomePage />} />
              </Route>

              <Route path="application" element={<ApplicationGuard />}>
                <Route index element={<ApplicationPage />} />
                <Route path="calculator">
                  <Route index element={<CalculatorPage />} />
                  <Route path=":tab" element={<CalculatorPage />} />
                </Route>
                <Route
                  path="installment-confirmation"
                  element={<InstallmentConfirmationPage />}
                />
                <Route
                  path="credit-line-amount"
                  element={<CreditLineAmountPage />}
                />
                <Route
                  path="credit-line-confirmation"
                  element={<CreditLineConfirmationPage />}
                />
                <Route
                  path="credit-line-payment-date"
                  element={<CreditLinePaymentDatePage />}
                />
                <Route
                  path="derogation-declaration"
                  element={<DerogationDeclarationPage />}
                />
                <Route
                  path="alternative-offer"
                  element={<AlternativeOfferPage />}
                />
              </Route>

              <Route path="loan" element={<LoanGuard />}>
                <Route path="active" element={<ActiveLoanPage />} />
                <Route path="sign" element={<SignLoanPage />} />
              </Route>
            </Route>
          </Route>

          {/* Catch-all route for unmatched paths */}
          <Route path="*" element={<PublicGuard />} />
        </Route>
      </Routes>
    </>
  );
}

export default AppRouter;
